// src/components/impact/ImpactHero.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { TickerCarousel } from '@dopomogai/ui';
import { ImpactGridVisual } from '../../visuals/ImpactGridVisual'; // <-- IMPORT THE NEW VISUAL
import { ArrowDown, BookOpen } from 'lucide-react';

interface HeroData {
  badgeText: string;
  verbs: string[];
  nouns: string[];
  staticText: string;
  subheadline: string;
  ctaPrimary: string;
  ctaSecondary: string;
}

interface ImpactHeroProps {
  heroData: HeroData;
  onAction: (actionType: string, payload?: any) => void;
}

export const ImpactHero: React.FC<ImpactHeroProps> = ({ heroData, onAction }) => {
  if (!heroData || !heroData.verbs || !heroData.nouns) {
    return null;
  }

  return (
    <section className="relative bg-slate-900 text-white min-h-[90vh] md:min-h-[80vh] flex items-center py-24 md:py-32 overflow-hidden">
      {/* The background visual container */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, ease: "easeInOut" }}
      >
        <ImpactGridVisual /> {/* <-- USE THE NEW VISUAL */}
      </motion.div>
      
      <div className="container mx-auto px-4 text-center relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }} 
          className="flex flex-col items-center"
        >
          
          <Badge text={heroData.badgeText} accentColor="green" icon="heart" />

          <h1 className="mt-6 font-extrabold text-4xl md:text-5xl lg:text-6xl leading-tight drop-shadow-lg">
            <div className="h-20 flex justify-center">
              <TickerCarousel
                items={heroData.verbs}
                itemHeightRem={5}
                intervalSeconds={2.2}
                activeColor="text-emerald-400"
                inactiveColor="text-slate-500"
              />
            </div>
            <div className="h-20 flex justify-center">
              <TickerCarousel
                items={heroData.nouns}
                itemHeightRem={5}
                intervalSeconds={2.5}
                activeColor="text-emerald-400"
                inactiveColor="text-slate-500"
              />
            </div>
            <span className="text-white">{heroData.staticText}</span>
          </h1>

          <p className="mt-6 max-w-3xl mx-auto text-lg md:text-xl text-slate-300">
            {heroData.subheadline}
          </p>
          
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              text={heroData.ctaPrimary}
              variant="primary"
              size="lg"
              icon={<ArrowDown size={18} />}
              onClick={() => onAction('SCROLL_TO_SECTION', { targetId: 'initiatives-section' })}
              className="w-full sm:w-auto hover:shadow-[4px_4px_0_0_#10b981]"
            />
            <Button
              text={heroData.ctaSecondary}
              variant="secondary"
              size="lg"
              icon={<BookOpen size={18} />}
              onClick={() => onAction('OPEN_URL', { url: 'https://impact.dpmg.xyz', newTab: true })}
              className="w-full sm:w-auto bg-slate-700 text-white hover:bg-slate-600 hover:shadow-[4px_4px_0_0_#9ca3af]"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

